import {
  doc,
  updateDoc,
  setDoc,
  getDocs,
  collection,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config"; // adjust path if needed
import { checkAndUpdateLevel } from "../utils/Utils";

/**
 * Generate a card pack with a mix of character cards and infinity cards.
 * @param {array} characterCards - Character Cards array
 * @param {object} infinityCards - Character Cards array
 * @returns {array} Returns a card pack.
 */
export function generateCardPack(infinityCards, characterCards) {
  //   console.log("⚙️ Generating Card Pack...");
  //   console.log("Character Cards Available:", characterCards.length);
  //   console.log("Infinity Cards Available:", infinityCards.length);

  const numCharacterCards = Math.min(getRandomInt(1, 2), characterCards.length);
  const numUniqueInfinityCards = getRandomInt(4, 7); // 5-8 unique total

  // Step 1: Unique picks
  const selectedCharacterCards = weightedSample(
    characterCards,
    numCharacterCards,
    getCharacterCardWeight
  );

  const selectedInfinityCards = weightedSample(
    infinityCards,
    numUniqueInfinityCards,
    getInfinityCardWeight
  );

  // Step 2: Fill rest of pack (with duplicates) using ONLY Infinity cards
  const finalPack = [...selectedCharacterCards];

  while (finalPack.length < 10) {
    const randomInfinityCard =
      selectedInfinityCards[
      Math.floor(Math.random() * selectedInfinityCards.length)
      ];
    finalPack.push({ ...randomInfinityCard });
  }

  // Step 3: Shuffle final pack (optional, for random positioning)
  const shuffledPack = shuffleArray(finalPack);

  // Step 4: Logs
  const characterCount = shuffledPack.filter(
    (c) => c.type === "character"
  ).length;
  const infinityCount = shuffledPack.filter(
    (c) => c.type === "Infinity"
  ).length;

  console.log("🎯 Character Count:", characterCount);
  console.log("🎯 Infinity Count:", infinityCount);
  //   console.log("🎁 Final Pack (10 cards):", shuffledPack);

  const packWithUUIDs = shuffledPack.map((card) => ({
    ...card,
    _uuid: `${Date.now()}-${Math.random()}`,
  }));

  return packWithUUIDs;
}

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function weightedSample(cards, count, weightFn) {
  //   console.log("🔍 weightedSample → count:", count);
  if (!cards || cards.length === 0) {
    console.warn("⚠️ No cards provided to sample from.");
    return [];
  }

  const weighted = cards.map((card, i) => {
    const weight = weightFn(card);
    // console.log(`  🧪 [${i}] ${card.name || "Unnamed"} → Weight: ${weight}`);
    return { card, weight };
  });

  const sampled = [];
  let safety = 0;
  while (sampled.length < count && safety < 100) {
    const totalWeight = weighted.reduce((sum, w) => sum + w.weight, 0);
    let rand = Math.random() * totalWeight;

    for (let { card, weight } of weighted) {
      rand -= weight;
      if (rand <= 0) {
        if (!sampled.includes(card) || cards.length <= count) {
          sampled.push(card);
          //   console.log(`    🎯 Selected: ${card.name || "Unnamed"}`);
        }
        break;
      }
    }

    safety++;
  }

  if (safety >= 100) {
    console.warn(
      "⚠️ weightedSample: Reached safety limit. Potential infinite loop."
    );
  }

  //   console.log("✅ Sampled Result:", sampled);
  return sampled;
}

function getCharacterCardWeight(card) {
  const rarityWeights = {
    Common: 15,
    Uncommon: 10,
    Rare: 4,
    Epic: 2,
    Legendary: 1,
  };
  const weight = rarityWeights[card.rarity] || 1;
  //   console.log(
  //     `📦 getCharacterCardWeight(${card.name || "Unnamed"}): ${weight}`
  //   );
  return weight;
}

function getInfinityCardWeight(card) {
  let baseWeight = card.finite ? 2 : 5;

  if (card.max !== undefined && card.max !== null) {
    const max = Math.max(1, card.max);
    baseWeight *= Math.min(10, Math.ceil(max / 10));
  }

  //   console.log(
  //     `📦 getInfinityCardWeight(${card.name || "Unnamed"}): ${baseWeight}`
  //   );
  return baseWeight;
}

function getRandomInt(min, max) {
  const rand = Math.floor(Math.random() * (max - min + 1)) + min;
  //   console.log(`🎲 getRandomInt(${min}, ${max}) = ${rand}`);
  return rand;
}

export const canCraftCard = (card, playerCards) => {
  if (card.finite !== "TRUE") return false;

  const requiredName = card.craftNeeded;
  const requiredAmount = parseInt(card.craftAmount, 10);

  const matchingCard = playerCards.find((c) => c.name === requiredName);

  if (!matchingCard) return false;

  return matchingCard.amountOwned >= requiredAmount;
};

export const getCraftableCount = (card, playerCards) => {
  if (card.finite !== "TRUE") return 0;

  const requiredName = card.craftNeeded;
  const requiredAmount = parseInt(card.craftAmount, 10);

  const matchingCard = playerCards.find((c) => c.name === requiredName);

  if (!matchingCard) return 0;
  return Math.floor(matchingCard.amountOwned / requiredAmount);
};

export const isUsedToCraftFinite = (cardName, craftableCards) => {
  return craftableCards.some(
    (finiteCard) => finiteCard.craftNeeded === cardName
  );
};

export const getCraftAmount = (card) => {
  if (card.finite !== "TRUE") return 0;
  return parseInt(card.craftAmount, 10);
};

export const handleCraft = async (
  finiteCard,
  materialCard,
  count,
  userData
) => {
  const requiredAmount = parseInt(finiteCard.craftAmount, 10) * count;

  try {
    const userCardsRef = collection(db, "users", userData.id, "Infinity cards");

    // 1. Update the material card (by ID)
    const materialRef = doc(userCardsRef, materialCard.id);
    await updateDoc(materialRef, {
      amountOwned: materialCard.amountOwned - requiredAmount,
    });

    // 2. Query to find if user already has the finite card
    const craftedQuery = query(
      userCardsRef,
      where("name", "==", finiteCard.name)
    );

    const querySnapshot = await getDocs(craftedQuery);

    if (!querySnapshot.empty) {
      // Found the finite card — update it
      const craftedDoc = querySnapshot.docs[0];
      const currentAmount = craftedDoc.data().amountOwned || 0;

      await updateDoc(craftedDoc.ref, {
        amountOwned: currentAmount + count,
      });
    } else {
      // User doesn't own it yet — create it
      const newDocRef = doc(userCardsRef); // auto-ID
      await setDoc(newDocRef, {
        ...finiteCard,
        amountOwned: count,
      });
    }

    console.log(`✅ Crafted ${count}x ${finiteCard.name}`);
  } catch (err) {
    console.error("❌ Crafting failed:", err);
  }
};

export const handleDestroy = async (card, count, userData) => {
  const cardRef = doc(db, "users", userData.id, "Infinity cards", card.id);
  const userRef = doc(db, "users", userData.id);

  try {
    // Update card
    await updateDoc(cardRef, {
      amountOwned: card.amountOwned - count,
    });

    // Update XP
    const userSnap = await getDoc(userRef);
    const currentXP = userSnap.exists() ? userSnap.data()?.xp || 0 : 0;

    const updatedXp = currentXP + count * 10;

    await updateDoc(userRef, {
      xp: updatedXp,
    });

    await checkAndUpdateLevel(userData, updatedXp);

    console.log(`🔥 Destroyed ${count} ${card.name} for ${count * 10} XP`);
  } catch (err) {
    console.error("❌ Destroy failed:", err);
  }
};
